import { Box, Button, Checkbox, Flex, Image, Text, Textarea } from '@chakra-ui/react';
import Logo from 'assets/logowhite.png';
import ProgressCounter from 'components/Questions/ProgressCounter';
import useEvaluation from 'hooks/useEvaluation';
import React from 'react';
import { FieldError, SubmitErrorHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import allQuestions from './questions.json';

const maxPages = 3;
const maxSelected = 10;

interface IParamsProps {
  setor_id: string;
  id: string;
  time: string;
}

type TForm = Record<string, string[]>;

function getAnswers(data: TForm) {
  const final: { question: string; response: string }[] = [];
  Object.entries(data).forEach(([pIndex, values]) => {
    values.forEach((value, qIndex) => final.push({ question: `${pIndex}.${qIndex}`, response: value }));
  });
  return final;
}

export default function QuestionAlinhamentoDeValores() {
  const { id } = useParams<IParamsProps>();
  const { handleSubmit, register, watch } = useForm<TForm>({
    shouldFocusError: true,
    defaultValues: {
      // '0': ['0', '1', '2', '3', '4', '5', '6', '12', '8', '9'],
      // '1': ['0', '1', '2', '3', '4', '5', '6', '12', '8', '9'],
      // '2': ['a', 'b'],
      '0': [],
      '1': [],
      '2': ['', ''],
    },
  });
  const { registerResponses, handlePrevious, page, handleNext, evaluationData } = useEvaluation({ id });

  const questions = allQuestions[evaluationData?.type_evaluation !== '15' ? 'manager' : 'employee'];

  async function onSubmit(data: TForm) {
    if (page < 2 && (!data[page] || data[page].length < maxSelected)) {
      return toast.error(`Por favor, assinale dez opções! Você marcou ${data[page].length || 0} de ${maxSelected}`);
    }

    if (page < maxPages - 1) return handleNext();

    const answers = getAnswers(data);
    await registerResponses(answers);
  }

  const handleInvalid: SubmitErrorHandler<TForm> = (evt) => {
    const pages = Object.values(evt) as unknown as FieldError[][];
    pages.forEach((page) => {
      if (!page) return;
      page.forEach((question: FieldError) => {
        if (!question) return;
        toast.error(question.message);
      });
    });
  };

  return (
    <Flex w="100%" h="100vh" justify="center">
      <Box maxWidth={['100%', '100%', '62.5rem']}>
        <Flex
          justify="center"
          w={['100%', '100%', '62.5rem']}
          bg="#f8bb03"
          flexDirection={['column']}
          padding="1.25rem"
        >
          <Flex justify="space-around" align="center" w={['100%', '100%', '62.5rem']} flexDirection={['column', 'row']}>
            <Image
              flexShrink={0}
              w={['11.25rem', '14.375rem']}
              h={['8.125rem', '9.375rem']}
              src={Logo}
              alt="Logo White"
            />
            <Text color="#FFF" fontSize={['1.5rem', '2.3rem']} lineHeight="100%">
              Identificação e Alinhamento <br /> de Valores e Necessidades
            </Text>
          </Flex>
          <Box marginX="2.5rem" marginTop="1.25rem">
            <Text align="left" fontWeight="600" textAlign="justify">
              Este teste tem como objetivo identificar e comparar os valores e necessidades da empresa e do colaborador
              (ou candidato), avaliando o alinhamento entre expectativas individuais e cultura organizacional. A análise
              apoia decisões mais assertivas em contratações, promoções e desenvolvimento, fortalecendo a conexão entre
              o que a empresa valoriza e o que o profissional busca. Isso favorece um ambiente mais harmônico, engajado
              e produtivo, com maior alinhamento entre propósito, bem-estar e resultados.
            </Text>
          </Box>
        </Flex>
        <Flex w={['100%', '100%', '62.5rem']} marginBottom="2.5rem" bg="#FFF" flexDirection="column">
          <Flex
            paddingInline="2.5rem"
            flexDir="column"
            as="form"
            gap={4}
            onSubmit={handleSubmit(onSubmit, handleInvalid)}
            mb="6.75rem"
          >
            <Flex margin="0.9375rem 0" marginTop={2}>
              <Text fontSize="1.25rem">
                <span
                  dangerouslySetInnerHTML={{
                    __html: `<b>Questionário ${page + 1} - ${questions[page].title}</b> <br/> ${
                      questions[page].introduction
                    }`,
                  }}
                ></span>
              </Text>
            </Flex>

            {questions[page].questions.map((question, index) => (
              <Checkbox
                {...register(`${page}`)}
                defaultChecked={false}
                isChecked={watch(`${page}`).includes(index.toString())}
                disabled={watch(`${page}`).length >= maxSelected && !watch(`${page}`).includes(index.toString())}
                value={index}
                margin="0"
                gap="0.5rem"
                key={`${question.text}-${index}`}
              >
                <strong>{question.title}</strong>
                {': '}
                <span>{question.text}</span>
              </Checkbox>
            ))}

            {questions[page].openQuestions.map((question, index) => {
              return (
                <React.Fragment key={`${question}-${index}`}>
                  <Flex margin="1rem 0">
                    <Text fontSize="1.25rem">{question}</Text>
                    {index === 0 && (
                      <Text color="red" marginLeft="0.625rem">
                        *
                      </Text>
                    )}
                  </Flex>
                  <Textarea
                    w="100%"
                    margin="0rem 0rem 0 0"
                    size="sm"
                    resize="none"
                    placeholder="Insira sua resposta"
                    defaultValue=""
                    {...register(`${page}.${index}`, index === 0 ? { required: 'Campo obrigatório' } : {})}
                  />
                </React.Fragment>
              );
            })}

            <Flex justifyContent="center" position="fixed" left={0} bottom={0} bg="white" w="100%" zIndex={1050}>
              <Box w={['100%', '100%', '62.5rem']}>
                <ProgressCounter
                  maxWidth={['100%', '100%', '62.5rem']}
                  width={'100%'}
                  text="Etapa"
                  counter={page + 1}
                  total={maxPages}
                  bg="white"
                >
                  <Button
                    w="10rem"
                    fontWeight="500"
                    fontSize="1.3rem"
                    onClick={handlePrevious}
                    disabled={page === 0}
                    variant="secondary"
                  >
                    Voltar
                  </Button>
                  <Button variant="primary" type="submit" w="10rem" fontWeight="500" fontSize="1.3rem">
                    {page < maxPages - 1 ? 'Avançar' : 'Enviar'}
                  </Button>
                </ProgressCounter>
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
}
